import { Alert, Box, Link, Snackbar, Stack, TextField, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Link as RouterLink } from "react-router-dom"
import { ACTIVATION_CODE_ROUTE, AUTH_PASSWORD_ROUTE, FORGOT_PASSWORD_ROUTE } from "../../../constants/routes"
import { Field, Form, Formik, FormikHelpers } from "formik"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { FormikCheckbox } from "../../../shared/components/Formik/FormikCheckbox"
import { FormikSubmitButton } from "../../../shared/components/forms/Formik"
import * as yup from "yup"
import { useAppDispatch, useAppSelector } from "../../store/storeHooks"
import { selectRememberMe, selectUsername } from "../loginSelectors"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { useState } from "react"
import { isRequiresActivationCodeResponse, isRequiresPasswordResponse } from "../loginTypes"
import { setUsername } from "../loginSlice"
import { SignInWithPasswordState } from "../pages/SignInWithPassword"
import { useNavigate } from "react-router"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import { authenticateByUsername } from "../loginApi"

const useStyles = makeStyles()(() => ({
    formContainer: {
        maxWidth: "25rem",
        width: "100%",
        margin: "auto",
    },
    welcomeText: {
        textAlign: "left",
        marginBottom: "0.5rem",
        fontWeight: 400,
        fontSize: "2.5rem",
    },
    welcomeSubText: {
        textAlign: "left",
        marginBottom: "1rem",
        color: "rgba(0, 0, 0, 0.6)",
    },
    submitButton: {
        marginTop: "1rem",
        padding: "0.75rem",
        backgroundColor: "#000",
        color: "#fff",
        borderRadius: "3rem",
        "&:hover": {
            backgroundColor: "#333",
        },
    },
}))

type SignInWithUserNameProps = {}

type FormValues = {
    username: string
    rememberUsername: boolean
}

export function SignInWithUserName({}: SignInWithUserNameProps) {
    const { classes } = useStyles()
    const username = useAppSelector(selectUsername)
    const rememberMe = useAppSelector(selectRememberMe)
    const dispatch = useAppDispatch()
    const { l10n } = useAppLocalization()

    const validationSchema = yup.object({
        username: yup
            .string()
            .min(3, l10n.getString("sign-in-with-username-min-length"))
            .required(l10n.getString("sign-in-with-username-required")),
        rememberUsername: yup.boolean(),
    })

    const initialValues: FormValues = {
        username: username ?? "",
        rememberUsername: rememberMe ?? false,
    }

    const { mutateAsync: callAuthenticateByUsername } = usePublicApiMutation(authenticateByUsername, {})

    const [showErrorToaster, setShowErrorToaster] = useState(false)
    const [errorMessage, setErrorMessage] = useState("Something went wrong")

    const navigate = useNavigate()

    const onSubmit = async ({ username, rememberUsername }: FormValues, helpers: FormikHelpers<FormValues>) => {
        const response = await callAuthenticateByUsername({ username })

        if (isRequiresPasswordResponse(response)) {
            dispatch(setUsername({ username, rememberMe: rememberUsername }))
            const navState: SignInWithPasswordState = { authentication: response.authentication }
            navigate(AUTH_PASSWORD_ROUTE, { state: navState })
        } else if (isRequiresActivationCodeResponse(response)) {
            dispatch(setUsername({ username, rememberMe: rememberUsername }))
            const navState: SignInWithPasswordState = { authentication: response.authentication }
            navigate(ACTIVATION_CODE_ROUTE, { state: navState })
        } else {
            setShowErrorToaster(true)
            setErrorMessage(
                `Client expected authentication complete but server said: ${response.authenticationResult.action}`,
            )
        }
    }

    return (
        <Box className={classes.formContainer}>
            <Formik<FormValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {({ errors, touched, handleSubmit, isValid }) => (
                    <Box component={Form} onSubmit={handleSubmit}>
                        <LocalizedStrict id="sign-in-with-username-title">
                            <Typography component="h1" variant="h3" className={classes.welcomeText}>
                                Welcome
                            </Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="sign-in-with-username-subtitle">
                            <Typography component="p" className={classes.welcomeSubText}>
                                Welcome to Facilitynet
                            </Typography>
                        </LocalizedStrict>
                        <Stack direction="column" spacing=".25rem">
                            <LocalizedStrict
                                id="sign-in-with-username-field-label"
                                attrs={{ placeholder: true, label: true }}
                            >
                                <Field
                                    as={TextField}
                                    name="username"
                                    label="Username"
                                    placeholder="Enter your username"
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    error={touched.username && !!errors.username}
                                    helperText={touched.username && errors.username}
                                    autoComplete="username"
                                />
                            </LocalizedStrict>

                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <LocalizedStrict id="sign-in-with-username-remember-me">
                                    <FormikCheckbox name="rememberUsername" color="primary">
                                        Remember me
                                    </FormikCheckbox>
                                </LocalizedStrict>
                                <LocalizedStrict id="sign-in-with-username-reset-password-link">
                                    <Link component={RouterLink} to={FORGOT_PASSWORD_ROUTE} variant="body2">
                                        Forgot password?
                                    </Link>
                                </LocalizedStrict>
                            </Stack>
                        </Stack>
                        <LocalizedStrict id="sign-in-with-username-submit-button">
                            <FormikSubmitButton
                                className={classes.submitButton}
                                variant="contained"
                                sx={{ marginTop: 3, marginBottom: 2 }}
                            >
                                Next
                            </FormikSubmitButton>
                        </LocalizedStrict>
                    </Box>
                )}
            </Formik>
            <Snackbar
                open={showErrorToaster}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                autoHideDuration={5000}
                onClose={() => setShowErrorToaster(false)}
            >
                <Alert
                    onClose={() => setShowErrorToaster(false)}
                    sx={{ marginBottom: "2rem", marginRight: "1rem" }}
                    severity="error"
                    variant="filled"
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    )
}
